import { useState, type FormEvent } from "react";
import { ApiError, api } from "./api";
import { errorKey, useT } from "./i18n";

/** Minimum length, same as the backend. Checked here only to warn sooner. */
const MIN = 10;

export default function ResetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const t = useT();
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (password !== repeat) {
      setError(t("reset.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (err) {
      setError(t(err instanceof ApiError ? errorKey(err.code) : "error.password_change_failed"));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-5">
        <img src="/icon.svg" alt="" width="44" height="44" className="mb-4 rounded-[10px]" />
        <h1 className="text-3xl font-semibold tracking-tight">{t("reset.readyTitle")}</h1>
        <p className="mt-2 opacity-70">{t("reset.done")}</p>
        <button
          type="button" onClick={onDone}
          className="mt-6 rounded bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white"
        >
          {t("auth.signIn")}
        </button>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-sm flex-col justify-center px-5">
      <img src="/icon.svg" alt="" width="44" height="44" className="mb-4 rounded-[10px]" />
      <h1 className="text-3xl font-semibold tracking-tight">{t("reset.title")}</h1>
      <p className="mt-2 opacity-70">{t("reset.minHint", { min: MIN })}</p>

      <form onSubmit={submit} className="mt-8 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest opacity-60">
            {t("reset.new")}
          </span>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required minLength={MIN} autoComplete="new-password" autoFocus
            className="rounded border border-current/25 bg-transparent px-3 py-2.5"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold uppercase tracking-widest opacity-60">
            {t("reset.confirm")}
          </span>
          <input
            type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)}
            required minLength={MIN} autoComplete="new-password"
            className="rounded border border-current/25 bg-transparent px-3 py-2.5"
          />
        </label>

        {error && <p role="alert" className="text-[var(--color-bad)]">{error}</p>}

        <button
          type="submit" disabled={busy}
          className="mt-2 rounded bg-[var(--color-accent)] px-5 py-2.5 font-semibold text-white disabled:opacity-50"
        >
          {t(busy ? "reset.saving" : "reset.submit")}
        </button>
      </form>
    </main>
  );
}
